import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import startCase from 'lodash/startCase';
import { Text } from 'office-ui-fabric-react';
import { isNil } from 'lodash';
import DetailsList from '../DetailsList';
import styles from './AdjustmentsList.style';
import { formatDateString, formatNumberWithCommas } from '../../utils';
import AdjustmentStatusIcons from '../AdjustmentStatusIcons';
import {
  getCalculatedAdjustmentStatus,
  getAdvancePaymentTotalString,
} from '../../utils/adjustmentUtils';
import AdjustmentListPaymentStatus from './AdjustmentListPaymentStatus';

const AdjustmentsList = ({
  items,
  className: classNameProp,
  disabled,
  hiddenColumns,
  perLineItem,
  ...other
}) => {
  let columns = [
    {
      key: 'status',
      name: 'Status',
      minWidth: 140,
      maxWidth: 140,
      headerClassName: styles.iconColumnHeader,
      iconClassName: styles.headerIcon,
      ariaLabel: 'Status',
      iconName: 'Product',
      isIconOnly: true,
      fieldName: 'status',
      className: styles.alignLeft,
      columnActionsMode: 0,
      onRender(adjustment) {
        return (
          <AdjustmentStatusIcons status={getCalculatedAdjustmentStatus(adjustment, perLineItem)} />
        );
      },
    },
    {
      key: 'name',
      name: 'Name',
      fieldName: 'name',
      isResizable: true,
      minWidth: 110,
      className: styles.alignLeft,
      onRender(adjustment) {
        return (
          <Text styles={{ root: { whiteSpace: 'pre-wrap', fontWeight: 400, fontSize: '0.75rem' } }}>
            {adjustment.name}
          </Text>
        );
      },
    },
    {
      key: 'type',
      name: 'Type',
      fieldName: 'type',
      isResizable: true,
      maxWidth: 200,
      minWidth: 130,
      className: styles.alignLeft,
      onRender: adjustment => startCase(adjustment.type),
    },
    {
      key: 'contractId',
      name: 'Contract Id',
      fieldName: 'contractId',
      isResizable: true,
      maxWidth: 200,
      minWidth: 70,
      className: styles.alignLeft,
    },
    {
      key: 'totalAmount',
      name: (
        <div style={{ display: 'flex' }}>
          <div className={styles.alignAmountHeader}>Amount</div>
        </div>
      ),
      fieldName: 'totalAmount',
      isResizable: true,
      minWidth: 100,
      className: styles.alignAmount,
      onRender(adjustment) {
        // if amount is negative, show in red with parentheses
        if (adjustment.amount < 0) {
          const currency = adjustment?.currency?.code || adjustment?.currency;
          return (
            <span className={styles.negativeAmount}>
              {`(${formatNumberWithCommas(Math.abs(adjustment.amount).toFixed(2))} ${currency})`}
            </span>
          );
        }
        return <span>{`${adjustment.totalAmount}`}</span>;
      },
    },
    {
      key: 'createdBy',
      name: 'Created By',
      isResizable: true,
      maxWidth: 120,
      className: styles.alignLeft,
      onRender: adjustment => adjustment.creator?.name,
    },
    {
      key: 'dateCreated',
      name: 'Created',
      fieldName: 'createdTimestamp',
      isResizable: true,
      className: styles.alignLeft,
      maxWidth: 100,
      minWidth: 60,
      onRender: adjustment =>
        adjustment.createdTimestamp ? `${formatDateString(adjustment.createdTimestamp)}` : '',
    },
    {
      key: 'dateApproved',
      name: 'Approved',
      fieldName: 'approvedTimestamp',
      isResizable: true,
      className: styles.alignLeft,
      maxWidth: 100,
      minWidth: 70,
      onRender: adjustment =>
        adjustment.approvedTimestamp ? `${formatDateString(adjustment.approvedTimestamp)}` : '',
    },
    {
      key: 'dueDate',
      name: 'Due',
      fieldName: 'dueDate',
      isResizable: true,
      className: styles.alignLeft,
      maxWidth: 100,
      minWidth: 55,
      onRender: adjustment => (adjustment.dueDate ? `${formatDateString(adjustment.dueDate)}` : ''),
    },
    {
      key: 'paymentStatus',
      name: 'Payment Status',
      fieldName: 'paymentStatus',
      isResizable: true,
      className: styles.alignLeft,
      maxWidth: 100,
      minWidth: 90,
      onRender: adjustment =>
        adjustment.paymentStatus
          ? AdjustmentListPaymentStatus({ paymentStatus: adjustment.paymentStatus })
          : '',
    },
  ];

  const className = clsx(styles.root, classNameProp);

  const getAdjustmentAmount = adjustment => {
    if (perLineItem) {
      const currency = adjustment?.currency?.code || adjustment?.currency;
      if (isNil(adjustment.amount)) return '';

      return `${formatNumberWithCommas(adjustment.amount.toFixed(2))} ${currency}`;
    }
    return getAdvancePaymentTotalString(adjustment.lineItems, styles, true);
  };

  // only show due date and payment status if adjustments are returned as individual lineItems
  if (!perLineItem) hiddenColumns.push('dueDate', 'paymentStatus');

  if (hiddenColumns && hiddenColumns.length > 0) {
    columns = columns.filter(column => !hiddenColumns.includes(column.key));
  }

  return (
    <div data-automationid="adjustment-detail-list">
      <DetailsList
        items={items.map(adjustment => ({
          ...adjustment,
          key: perLineItem ? adjustment.lineItemId : adjustment._id,
          link: `/adjustments/${adjustment._id}`,
          dataAutomationId: `row-${adjustment._id}`,
          totalAmount: hiddenColumns.includes('totalAmount')
            ? 0.0
            : getAdjustmentAmount(adjustment),
        }))}
        columns={columns}
        className={className}
        // compactRows is the styling that we apply in our code base which does not handle lots of edge cases
        // and should be deprecated at some point.
        // compact is the api used by fluent ui to make the list compact which is what our styling was trying
        // to achieve
        compactRows={false}
        compact
        {...other}
      />
    </div>
  );
};

AdjustmentsList.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.string,
      contractId: PropTypes.string,
      dataAutomationId: PropTypes.string,
    }),
  ),
  hiddenColumns: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.string,
  disabled: PropTypes.bool,
  perLineItem: PropTypes.bool,
};

AdjustmentsList.defaultProps = {
  items: [],
  hiddenColumns: [],
  className: '',
  disabled: false,
  perLineItem: false,
};

export default AdjustmentsList;
